import { useCallback, useRef } from "react";

import {
  COLLECTION_FILTER_IDS,
  COLLECTION_PARAMS,
} from "@sections/Collection/CollectionFilters/constants";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";
import {
  type PriceRange,
  type CollectionSectionSettings,
} from "@tablescape/state";

// Event enums
export enum CollectionAnalyticsEvents {
  CLICK_FILTER = "clickFilter",
  CLICK_SORT_BY = "clickSortBy",
  VIEW_PAGE = "viewPage",
}

// Type definitions for event data
export interface FilterEventData {
  action: "apply" | "remove" | "clear all";
  "filter type": string;
  "filter value": string;
  "from path": string;
  "from url": string;
  "previous filter value": string[];
}

export interface SortEventData {
  "from path": string;
  "from url": string;
  "sort value": string;
}

export interface PageViewEventData {
  market: string;
  path: string;
  url: string;
}

// Click deduplication state
interface ClickState {
  lastEventType: string | null;
  lastTarget: string | null;
  lastTimestamp: number;
}

// Helper functions
const getCurrentUrl = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.href;
};

const getCurrentPath = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
};

const generateClickId = (eventType: string, target: string): string => {
  return `${eventType}-${target}`;
};

const shouldFireEvent = (
  clickState: ClickState,
  eventType: string,
  target: string,
  minInterval: number = 100
): boolean => {
  const now = Date.now();
  const clickId = generateClickId(eventType, target);
  const lastClickId = generateClickId(
    clickState.lastEventType || "",
    clickState.lastTarget || ""
  );

  // Fire if it's a different event type, different target, or enough time has passed
  return (
    clickId !== lastClickId || now - clickState.lastTimestamp >= minInterval
  );
};

const updateClickState = (
  clickState: ClickState,
  eventType: string,
  target: string
): void => {
  clickState.lastEventType = eventType;
  clickState.lastTarget = target;
  clickState.lastTimestamp = Date.now();
};

// Main hook
export const useCollectionAnalytics = () => {
  const {
    meta: {
      localization: { country: market },
    },
  } = useSectionSettings<CollectionSectionSettings>();
  const clickStateRef = useRef<ClickState>({
    lastEventType: null,
    lastTarget: null,
    lastTimestamp: 0,
  });

  const fireEvent = useCallback(
    (
      eventType: string,
      data:
        | {
            action: "apply" | "remove" | "clear all";
            "filter type": string | null;
            "filter value": string | null;
            "from path": string;
            "from url": string;
            "previous filter value": string[];
          }
        | {
            "from path": string;
            "from url": string;
            "sort value": string;
          }
        | PageViewEventData
    ) => {
      if (typeof window !== "undefined" && window.maestro) {
        window.maestro.fireTrackEvent(eventType, {
          ...data,
          market, // Add market context to all events
        });
      }
    },
    [market]
  );

  // Helper method to get filter category from filter ID
  const getFilterCategory = useCallback((filterId: string) => {
    // Map filter IDs to their categories using COLLECTION_PARAMS enum
    if (filterId.startsWith(COLLECTION_FILTER_IDS.CAPACITY)) {
      return COLLECTION_PARAMS.CAPACITY;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.COLOR)) {
      return COLLECTION_PARAMS.COLOR;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.DIAMETER)) {
      return COLLECTION_PARAMS.DIAMETER;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.MATERIAL)) {
      return COLLECTION_PARAMS.MATERIAL;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.SIZE)) {
      return COLLECTION_PARAMS.SIZE;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.PRICE)) {
      return COLLECTION_PARAMS.PRICE;
    }

    // Fallback to original logic for unknown patterns
    return undefined;
  }, []);

  // Helper method to filter previous filter values by category
  const getPreviousFilterValues = useCallback(
    (appliedFilters: Array<{ id: string; label: string }>): string[] => {
      return appliedFilters.map((f) => f.label);
    },
    []
  );

  // Helper method to format price range for display
  const formatPriceRange = useCallback((priceRange: PriceRange): string => {
    return `$${priceRange.min} - $${priceRange.max || "∞"}`;
  }, []);

  const getReporters = useCallback(() => {
    const clickState = clickStateRef.current;

    return {
      // Filter toggle tracking (apply/remove from facet UI)
      trackFilterToggle: (
        isSelected: boolean,
        filterType: string,
        filterValue: string,
        previousFilterValues: string[]
      ) => {
        const target = `filter-${filterType}-${filterValue}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: isSelected ? "apply" : "remove",
            "filter type": filterType,
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Filter deselection tracking (from Applied Filters pills)
      trackFilterDeselection: (
        filterType: string,
        filterValue: string,
        previousFilterValues: string[]
      ) => {
        const target = `filter-pill-${filterType}-${filterValue}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "remove",
            "filter type": filterType,
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Clear all filters tracking
      trackClearAllFilters: (
        clearedFilters: Array<{ type: string; value: string }>,
        previousFilterValues: string[]
      ) => {
        const target = "clear-all-filters";

        // Only fire event if there are actually filters to clear
        if (
          clearedFilters.length > 0 &&
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          // Fire a single clear all event instead of individual events
          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "clear all",
            "filter type": null,
            "filter value": null,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Price filter change tracking
      trackPriceFilterChange: (
        priceRange: PriceRange,
        previousFilterValues: string[]
      ) => {
        const target = `price-filter-${priceRange.min}-${priceRange.max}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          const filterValue = formatPriceRange(priceRange);

          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "apply",
            "filter type": "Price",
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Price filter removal tracking
      trackPriceFilterRemoval: (
        priceRange: PriceRange,
        previousFilterValues: string[]
      ) => {
        const target = `price-filter-remove-${priceRange.min}-${priceRange.max}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          const filterValue = formatPriceRange(priceRange);

          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "remove",
            "filter type": "Price",
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Sort option change tracking
      trackSortChange: (sortValue: string) => {
        const target = `sort-${sortValue}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_SORT_BY,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.CLICK_SORT_BY, {
            "sort value": sortValue,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_SORT_BY,
            target
          );
        }
      },

      // Page view tracking for collection navigation
      trackPageView: () => {
        const target = `page-view-${getCurrentPath()}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.VIEW_PAGE,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.VIEW_PAGE, {
            market,
            path: getCurrentPath(),
            url: getCurrentUrl(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.VIEW_PAGE,
            target
          );
        }
      },
    };
  }, [fireEvent, formatPriceRange, market]);

  return {
    market,
    ...getReporters(),
    // Expose helper methods for use in components
    getFilterCategory,
    getPreviousFilterValues,
    formatPriceRange,
  };
};
